import React, { useState, useEffect } from 'react';
import { AttentionSnapshot, InterruptMetrics } from '../types';
import './AttentionReport.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

interface AttentionReportProps {
  data: AttentionSnapshot;
  onExport: (format: 'json' | 'csv') => void;
  loading?: boolean;
}

interface Recommendation {
  icon: string;
  text: string;
  level: 'critical' | 'warning' | 'success' | 'info';
}

interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
}

const Tooltip: React.FC<TooltipProps> = ({ children, content }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        style={{ cursor: 'help' }}
      >
        {children}
      </div>
      {isVisible && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginTop: '8px',
          backgroundColor: '#1f2937',
          color: '#fff',
          padding: '12px 16px',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          zIndex: 1000,
          width: '320px',
          fontSize: '0.875rem',
          lineHeight: '1.5'
        }}>
          {content}
          <div style={{
            position: 'absolute',
            top: '-6px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderBottom: '6px solid #1f2937'
          }} />
        </div>
      )}
    </div>
  );
};

const AttentionReport: React.FC<AttentionReportProps> = ({ data, onExport, loading = false }) => {
  const [selectedPerson, setSelectedPerson] = useState<InterruptMetrics | null>(null);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Fetch AI recommendations when person is selected
  useEffect(() => {
    if (!selectedPerson) {
      setRecommendations([]);
      return;
    }

    const fetchRecommendations = async () => {
      setLoadingRecs(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/recommendations/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(selectedPerson)
        });
        const result = await response.json();
        setRecommendations(result.recommendations || []);
      } catch (error) {
        console.error('Failed to fetch recommendations:', error);
        setRecommendations([{
          icon: '⚠️',
          text: 'Failed to load AI recommendations. Please try again.',
          level: 'warning'
        }]);
      } finally {
        setLoadingRecs(false);
      }
    };

    fetchRecommendations();
  }, [selectedPerson]);

  // Helper to format minutes to readable time
  const formatMinutes = (minutes: number | null | undefined) => {
    if (minutes === null || minutes === undefined) return '--';
    if (minutes === 0) return '0m';
    if (minutes < 60) return `${Math.round(minutes)}m`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  // Helper to get trend icon
  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'rising': return '↑';
      case 'falling': return '↓';
      default: return '→';
    }
  };

  // Helper to get hot zone badge color
  const getHotZoneBadgeClass = (score: number) => {
    if (score >= 0.7) return 'flag-chip critical';
    if (score >= 0.5) return 'flag-chip warning';
    return 'flag-chip ok';
  };

  // Handle export with error handling
  const handleExport = async (format: 'json' | 'csv') => {
    try {
      setExportError(null);
      await onExport(format);
    } catch (error) {
      console.error('Export failed:', error);
      setExportError(`Failed to export ${format.toUpperCase()}. Please try again.`);
      setTimeout(() => setExportError(null), 5000); // Clear error after 5 seconds
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="attention-report-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading attention analytics...</p>
        </div>
      </div>
    );
  }

  // Show empty state if no data
  if (!data || !data.interrupts_per_person || data.interrupts_per_person.length === 0) {
    return (
      <div className="attention-report-container">
        <div className="empty-state-full">
          <div className="empty-icon">📊</div>
          <h2>No Data Available</h2>
          <p>Process messages to generate attention analytics.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="attention-report-container">
      {/* Hero Bar - Metrics only, no navigation */}
      <div className="report-hero-bar">
        <div className="hero-content">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 className="hero-metric">{data.hero_stat}</h1>
            <Tooltip content={
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Priority Scoring System</div>
                <div style={{ marginBottom: '6px' }}><strong>U (Urgency):</strong> How time-sensitive is this task? Approaching deadlines and blocking issues score higher.</div>
                <div style={{ marginBottom: '6px' }}><strong>I (Impact):</strong> What's the business/team impact? Production outages, revenue impact, and customer-facing issues score higher.</div>
                <div style={{ marginBottom: '6px' }}><strong>C (Context):</strong> Current circumstances like who's involved, number of mentions, and conversation intensity.</div>
                <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.2)', fontSize: '0.8rem' }}>
                  Priority Score combines these factors to rank tasks from 0.0-1.0
                </div>
              </div>
            }>
              <span style={{
                cursor: 'help',
                fontSize: '1rem',
                opacity: 0.6,
                transition: 'opacity 0.2s'
              }}>ℹ️</span>
            </Tooltip>
          </div>
          <div className="hero-sub-metrics">
            <span className="sub-metric">
              <span className="metric-label">Avg between:</span>
              <span className="metric-value">{formatMinutes(data.avg_team_minutes_between)}</span>
            </span>
            <span className="sub-metric">
              <span className="metric-label">Deep blocks:</span>
              <span className="metric-value">{data.total_deep_work_blocks}</span>
            </span>
            <span className="sub-metric">
              <span className="metric-label">Response time:</span>
              <span className="metric-value">{formatMinutes(data.median_response_time_minutes)}</span>
            </span>
          </div>
        </div>
        <div className="hero-actions">
          {exportError && (
            <div className="export-error" style={{
              color: '#ef4444',
              fontSize: '0.875rem',
              marginRight: '1rem',
              padding: '0.5rem',
              backgroundColor: '#fee2e2',
              borderRadius: '4px'
            }}>
              {exportError}
            </div>
          )}
          <button className="export-btn primary" onClick={() => handleExport('json')}>
            Export Data
          </button>
        </div>
      </div>

      {/* Two-pane layout */}
      <div className="report-two-pane">
        {/* Left Pane: Person Cards */}
        <div className="left-pane">
          <div className="pane-header">
            <h2>Flow State Dashboard</h2>
            <span className="team-size">{data.interrupts_per_person.filter(person => person.user !== "Unassigned").length} members</span>
          </div>

          <div className="person-cards">
            {data.interrupts_per_person.filter(person => person.user !== "Unassigned").map((person) => (
              <div
                key={person.user}
                className={`person-card ${selectedPerson?.user === person.user ? 'selected' : ''}`}
                onClick={() => setSelectedPerson(person)}
              >
                <div className="person-header">
                  <span className="person-name">{person.user.split('@')[0]}</span>
                  <span className={getHotZoneBadgeClass(person.hot_zone_score)}>
                    {person.hot_zone_score >= 0.7 ? 'HOT ZONE' :
                     person.hot_zone_score >= 0.5 ? 'AT RISK' : 'PROTECTED'}
                  </span>
                </div>

                <div className="person-metrics">
                  <div className="metric-row">
                    <span className="metric-label">Interrupts</span>
                    <span className="metric-value">
                      {person.count}
                      <span className={`trend-icon ${person.trend}`}>
                        {getTrendIcon(person.trend)}
                      </span>
                    </span>
                  </div>
                  <div className="metric-row">
                    <span className="metric-label">Daily avg</span>
                    <span className="metric-value">{person.daily_avg.toFixed(1)}</span>
                  </div>
                  <div className="metric-row">
                    <span className="metric-label">Between</span>
                    <span className="metric-value">{formatMinutes(person.avg_minutes_between)}</span>
                  </div>
                </div>

                {/* Mini sparkline */}
                <div className="mini-sparkline">
                  <svg viewBox="0 0 100 20" className="sparkline-svg">
                    {person.interrupt_trends.slice(-7).map((trend, i, arr) => {
                      const maxCount = Math.max(...arr.map(t => t.count), 1);
                      const x = (i / (arr.length - 1)) * 90 + 5;
                      const y = 20 - (trend.count / maxCount) * 15;
                      return (
                        <circle
                          key={i}
                          cx={x}
                          cy={y}
                          r="2"
                          fill={person.trend === 'rising' ? '#ef4444' :
                                person.trend === 'falling' ? '#10b981' : '#6b7280'}
                        />
                      );
                    })}
                    {person.interrupt_trends.slice(-7).map((trend, i, arr) => {
                      if (i === 0) return null;
                      const maxCount = Math.max(...arr.map(t => t.count), 1);
                      const x1 = ((i - 1) / (arr.length - 1)) * 90 + 5;
                      const y1 = 20 - (arr[i - 1].count / maxCount) * 15;
                      const x2 = (i / (arr.length - 1)) * 90 + 5;
                      const y2 = 20 - (trend.count / maxCount) * 15;
                      return (
                        <line
                          key={`line-${i}`}
                          x1={x1}
                          y1={y1}
                          x2={x2}
                          y2={y2}
                          stroke={person.trend === 'rising' ? '#ef4444' :
                                 person.trend === 'falling' ? '#10b981' : '#6b7280'}
                          strokeWidth="1"
                          opacity="0.5"
                        />
                      );
                    })}
                  </svg>
                </div>

                <div className="person-footer">
                  <span className="chip small">
                    {person.deep_work_blocks} deep blocks
                  </span>
                  {person.response_latency_minutes && (
                    <span className="chip small">
                      {formatMinutes(person.response_latency_minutes)} response
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Pane: Deep Dive */}
        <div className="right-pane">
          {selectedPerson ? (
            <>
              <div className="pane-header">
                <h2>{selectedPerson.user.split('@')[0]}'s Focus Analysis</h2>
                <div className="header-badges">
                  <span className={`band-dot ${selectedPerson.hot_zone_score >= 0.7 ? 'red' :
                                             selectedPerson.hot_zone_score >= 0.5 ? 'yellow' : 'blue'}`}>
                    {selectedPerson.hot_zone_score.toFixed(2)} risk score
                  </span>
                </div>
              </div>

              {/* Interrupt Timeline */}
              <div className="analysis-section">
                <h3 className="section-title">Interrupt Timeline</h3>
                <div className="timeline-chart">
                  {selectedPerson.interrupt_trends.map((trend, i) => (
                    <div key={i} className="timeline-bar">
                      <span className="timeline-date">
                        {new Date(trend.date).toLocaleDateString('en-US', { weekday: 'short' })}
                      </span>
                      <div className="bar-container">
                        <div
                          className="bar-fill"
                          style={{
                            width: `${(trend.count / Math.max(...selectedPerson.interrupt_trends.map(t => t.count), 1)) * 100}%`,
                            backgroundColor: trend.count > 6 ? '#ef4444' :
                                           trend.count > 4 ? '#f59e0b' : '#10b981'
                          }}
                        />
                        <span className="bar-value">{trend.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Context Switch Analysis */}
              <div className="analysis-section">
                <h3 className="section-title">Context Switch Breakdown</h3>
                <div className="context-metrics">
                  <div className="context-metric">
                    <span className="metric-icon">⏱️</span>
                    <div className="metric-content">
                      <span className="metric-value">{formatMinutes(selectedPerson.avg_minutes_between)}</span>
                      <span className="metric-label">Avg between interrupts</span>
                    </div>
                  </div>
                  <div className="context-metric">
                    <span className="metric-icon">🎯</span>
                    <div className="metric-content">
                      <span className="metric-value">{selectedPerson.deep_work_blocks}</span>
                      <span className="metric-label">Deep work blocks ({'>'}2hr)</span>
                    </div>
                  </div>
                  <div className="context-metric">
                    <span className="metric-icon">⚡</span>
                    <div className="metric-content">
                      <span className="metric-value">{formatMinutes(selectedPerson.response_latency_minutes)}</span>
                      <span className="metric-label">Median response time</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* AI-Generated Recommendations */}
              <div className="analysis-section">
                <h3 className="section-title">AI Recommendations</h3>
                {loadingRecs ? (
                  <div className="recommendations-loading">
                    <div className="loading-spinner-small"></div>
                    <p>Generating personalized recommendations...</p>
                  </div>
                ) : (
                  <div className="recommendations">
                    {recommendations.map((rec, index) => (
                      <div key={index} className={`recommendation ${rec.level}`}>
                        <span className="rec-icon">{rec.icon}</span>
                        <p>{rec.text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">👈</div>
              <h3>Select a Team Member</h3>
              <p>Click on any team member card to view their detailed focus analysis and AI-powered recommendations</p>
            </div>
          )}
        </div>
      </div>

      {/* Recurring Themes Footer */}
      <div className="themes-footer">
        <h3>Recurring Interrupt Themes</h3>
        <div className="themes-pills">
          {data.recurring_themes.map((theme, i) => (
            <span key={i} className="theme-pill">
              {theme.label}
              <span className="theme-count">{theme.count}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AttentionReport;